/**
 * The colour swatches (SRS C4, docs/06-UI-SPEC.md §6.3).
 *
 * Twelve presets, a custom picker, and the six colours used most recently. Each
 * preset shows itself through its own token, so the palette re-skins with the theme
 * and with a school's branding without a line of it changing.
 */

import {
  hexColor,
  PEN_COLORS,
  RECENT_LIMIT,
  swatchColor,
  presetOf,
} from '../../board/palette.js';
import { useBoardStore } from '../../board/store.js';

interface SwatchProps {
  /** A token name, or a colour from the custom picker. */
  readonly choice: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

function Swatch({ choice, label, selected, onSelect }: SwatchProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={selected}
      // Keeps the caret in whatever is being typed into, so writing can be recoloured
      // mid-sentence and carry on.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onSelect}
      style={{ backgroundColor: swatchColor(choice) }}
      className={`aspect-square w-full rounded-full border transition-transform duration-fast hover:scale-110 ${
        selected ? 'border-text-primary ring-2 ring-primary' : 'border-border-strong'
      }`}
    />
  );
}

/** What a recent colour is called, so the row reads properly to a screen reader. */
function nameOf(choice: string): string {
  return presetOf(choice)?.name ?? choice;
}

function RecentRow({ onPick }: PaletteProps) {
  const recent = useBoardStore((state) => state.recentColors);
  const color = useBoardStore((state) => state.color);
  const pick = usePick(onPick);

  if (recent.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-text-secondary">Recent</span>
      <div className="grid grid-cols-6 gap-2">
        {recent.slice(0, RECENT_LIMIT).map((choice) => (
          <Swatch
            key={choice}
            choice={choice}
            label={`${nameOf(choice)}, used recently`}
            selected={choice === color}
            onSelect={() => {
              pick(choice);
            }}
          />
        ))}
      </div>
    </div>
  );
}

export interface PaletteProps {
  /**
   * What picking a colour does, when it should do more than set the pen.
   *
   * The select tool passes one in so that choosing a colour recolours whatever is
   * selected (SRS C4), which is what a teacher expects of a palette with work
   * already chosen.
   */
  readonly onPick?: (choice: string) => void;
}

/** Picking a colour: the tool's own handler if it gave one, the pen otherwise. */
function usePick(onPick: PaletteProps['onPick']): (choice: string) => void {
  const setColor = useBoardStore((state) => state.setColor);
  return onPick ?? setColor;
}

export function ColorPalette({ onPick }: PaletteProps = {}) {
  const color = useBoardStore((state) => state.color);
  const pick = usePick(onPick);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-text-secondary">Colour</h3>
        <label
          title="Custom colour"
          className="relative flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-dashed border-border-strong text-text-secondary hover:bg-surface-hover"
        >
          <span aria-hidden="true" className="text-base leading-none">
            +
          </span>
          <input
            type="color"
            aria-label="Custom colour"
            value={hexColor(color)}
            onChange={(event) => {
              pick(event.target.value);
            }}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </label>
      </div>

      <div className="grid grid-cols-6 gap-2">
        {PEN_COLORS.map((preset) => (
          <Swatch
            key={preset.token}
            choice={preset.token}
            label={preset.name}
            selected={preset.token === color}
            onSelect={() => {
              pick(preset.token);
            }}
          />
        ))}
      </div>

      <RecentRow onPick={onPick} />
    </section>
  );
}
